import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { assetUrl, formatApiErrorDetail } from "@/api";
import { useSiteContent } from "@/hooks/useSiteContent";
import { SERVICES } from "@/data/content";

const IMAGE_FIELDS = [
  { key: "hero_image", label: "Immagine principale Home (hero di sfondo)" },
  { key: "intro_image", label: "Immagine introduttiva Home" },
  ...SERVICES.map((s) => ({ key: `service_image_${s.id}`, label: `Servizio — ${s.fullTitle}` })),
];

export default function AdminImagesForm() {
  const { content, isLoading } = useSiteContent();
  const [busy, setBusy] = useState(null);
  const queryClient = useQueryClient();
  const inputRefs = useRef({});

  const uploadAndSave = async (key) => {
    const input = inputRefs.current[key];
    const file = input?.files?.[0];
    if (!file) {
      toast.error("Scegli prima un'immagine dal tuo dispositivo.");
      return;
    }
    setBusy(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd);
      await api.put("/admin/content", { [key]: up.url });
      queryClient.invalidateQueries({ queryKey: ["site-content"] });
      toast.success("Immagine aggiornata! Il sito è già aggiornato.");
      if (input) input.value = "";
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-7 w-7 animate-spin text-brand-cyan" />
      </div>
    );
  }

  return (
    <div className="grid gap-6 sm:grid-cols-2" data-testid="admin-images-form">
      {IMAGE_FIELDS.map((f) => (
        <div key={f.key} className="border border-white/10 bg-brand-card p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">{f.label}</p>
          <div className="mt-4 overflow-hidden border border-white/10">
            <img
              src={assetUrl(content[f.key])}
              alt={f.label}
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
              data-testid={`admin-image-preview-${f.key}`}
            />
          </div>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              ref={(el) => (inputRefs.current[f.key] = el)}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="w-full text-xs text-slate-400 file:mr-3 file:border file:border-white/20 file:bg-transparent file:px-3 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-white hover:file:border-brand-cyan"
              data-testid={`admin-image-file-${f.key}`}
            />
            <button
              onClick={() => uploadAndSave(f.key)}
              disabled={busy === f.key}
              data-testid={`admin-image-save-${f.key}`}
              className="flex shrink-0 items-center justify-center gap-2 bg-brand-cyan px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-teal transition hover:bg-brand-ice disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy === f.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              Carica
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
